import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  Box,
  MenuItem,
} from "@mui/material";
import { useSnackbar } from "components/GlobalSnackbar";
import { apiGetAllZones } from "api/zones";
import { apiGetAllCategories } from "api/categories";
import { apiGetHartaByKodHarta } from "api/harta";
import { getCurrentUserDepartment } from "utils/userUtils";
import { buttonStyles } from "utils/buttonStyles";
import { dialogHeaderStyles, dialogContentStyles } from "utils/dialogStyles";

function EditHarta({ open, data, onClose, onSave }) {
  const [formState, setFormState] = useState({
    zon: "",
    alamat1_harta: "",
    alamat2_harta: "",
    poskod_harta: "",
    bandar_harta: "",
    negeri_harta: "",
    kod_harta: "",
    kadar_sewa: "",
    kategori_kutipan: "",
    bahagian_pemantau: "",
  });

  const { showError } = useSnackbar();

  // State for zones dropdown
  const [zones, setZones] = useState([]);
  const [loadingZones, setLoadingZones] = useState(true);
  // State for kategori_kutipan dropdown
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  useEffect(() => {
    if (data) {
      setFormState({
        zon: data.zon || "",
        alamat1_harta: data.alamat1_harta || "",
        alamat2_harta: data.alamat2_harta || "",
        poskod_harta: data.poskod_harta || "",
        bandar_harta: data.bandar_harta || "",
        negeri_harta: data.negeri_harta || "",
        kod_harta: data.kod_harta || "",
        kadar_sewa: data.kadar_sewa || "",
        kategori_kutipan: data.kategori_kutipan || "",
        bahagian_pemantau: data.bahagian_pemantau || "",
      });
    }
  }, [data, open]);

  // Fetch zones and auto-fill user department on component mount
  useEffect(() => {
    const fetchZones = async () => {
      try {
        setLoadingZones(true);
        const response = await apiGetAllZones(1, 100); // Get first 100 zones
        if (response.data) {
          setZones(response.data);
        }
      } catch (error) {
        showError("Gagal memuatkan zon.");
        setZones([]);
      } finally {
        setLoadingZones(false);
      }
    };

    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const resp = await apiGetAllCategories(1, 1000);
        const list = Array.isArray(resp?.data) ? resp.data : [];
        const uniq = Array.from(
          new Map(list.map((c) => [String(c.kategori_kutipan || "").trim(), c])).values()
        )
          .filter((c) => (c.kategori_kutipan || "").trim() !== "")
          .sort((a, b) => String(a.kategori_kutipan).localeCompare(String(b.kategori_kutipan)));
        setCategories(uniq);
      } catch (error) {
        showError("Gagal memuatkan kategori kutipan.");
        setCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };

    const autoFillUserDepartment = () => {
      const userDepartment = getCurrentUserDepartment();
      setFormState((prevState) => ({
        ...prevState,
        bahagian_pemantau: userDepartment,
      }));
    };

    if (open) {
      fetchZones();
      fetchCategories();
      autoFillUserDepartment();
    }
  }, [open]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setFormState({
        zon: "",
        alamat1_harta: "",
        alamat2_harta: "",
        poskod_harta: "",
        bandar_harta: "",
        negeri_harta: "",
        kod_harta: "",
        kadar_sewa: "",
        kategori_kutipan: "",
        bahagian_pemantau: "",
      });
    }
  }, [open]);

  const handleChange = (e) => {
    setFormState({ ...formState, [e.target.name]: e.target.value });
  };

  // Map frontend formState to Prisma harta model shape
  function mapFrontendToPrismaHarta(formState) {
    return {
      kod_harta: formState.kod_harta || null,
      alamat1_harta: formState.alamat1_harta || null,
      alamat2_harta: formState.alamat2_harta || null,
      poskod_harta: formState.poskod_harta || null,
      bandar_harta: formState.bandar_harta || "",
      negeri_harta: formState.negeri_harta || null,
      zon: formState.zon || null,
      kadar_sewa: formState.kadar_sewa ? Number(formState.kadar_sewa) : null,
      bahagian_pemantau: formState.bahagian_pemantau || "",
      kategori_kutipan: formState.kategori_kutipan || "",
    };
  }

  const handleSave = async () => {
    try {
      // Duplicate kod_harta check: if kod_harta changed, verify it's not already used
      const newKod = (formState.kod_harta || "").trim();
      const oldKod = (data?.kod_harta || "").trim();
      if (!newKod) {
        showError("Kod Harta wajib diisi.");
        return;
      }
      if (newKod !== oldKod) {
        try {
          const resp = await apiGetHartaByKodHarta(newKod);
          if (resp && resp.data && resp.data.length > 0) {
            showError("Kod Harta sudah wujud. Sila pilih kod harta yang lain.");
            return;
          }
        } catch (err) {
          // If API returns 404, it means not found -> available; other errors should bubble
          if (String(err).includes("404")) {
            // ok
          } else {
            showError("Ralat semakan kod harta.");
            return;
          }
        }
      }
      const prismaHarta = mapFrontendToPrismaHarta(formState);
      await onSave(prismaHarta);
      onClose();
    } catch (error) {
      showError("Gagal mengemaskini rekod harta.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={dialogHeaderStyles.error}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" sx={{ color: "#fff" }}>Ubahsuai Rekod Harta</Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ ...dialogContentStyles.light, p: 2 }}>
        {/* Maklumat Lokasi Harta */}
        <Grid container spacing={2}>
          {/* Primary Property Info in 2-column layout */}
          <Grid item xs={12} md={6}>
            <TextField
              label="🏷️ Kod Harta"
              name="kod_harta"
              value={formState.kod_harta}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#fff",
                  fontSize: "0.875rem",
                  "&:hover fieldset": { borderColor: "#2196f3" },
                  "&.Mui-focused fieldset": { borderColor: "#2196f3" },
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>

          {/* Zone */}
          <Grid item xs={12} md={6}>
            <FormControl
              fullWidth
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#ffffffff",
                  fontSize: "0.875rem",
                  p: 1.2,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.875rem",
                },
              }}
            >
              <InputLabel>🎯 Zon Harta</InputLabel>
              <Select
                label="🎯 Zon Harta"
                name="zon"
                value={formState.zon}
                onChange={handleChange}
                fullWidth
              >
                <MenuItem value="">
                  <em>Pilih Zon</em>
                </MenuItem>
                {loadingZones ? (
                  <MenuItem value="" disabled>
                    Loading...
                  </MenuItem>
                ) : (
                  zones.map((zone) => (
                    <MenuItem key={zone.id} value={zone.zon}>
                      {zone.zon}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>

          {/* Address fields */}
          <Grid item xs={12}>
            <TextField
              label="📍 Alamat 1 Harta"
              name="alamat1_harta"
              value={formState.alamat1_harta}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#fff",
                  fontSize: "0.875rem",
                  "&:hover fieldset": { borderColor: "#2196f3" },
                  "&.Mui-focused fieldset": { borderColor: "#2196f3" },
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              label="📍 Alamat 2 Harta"
              name="alamat2_harta"
              value={formState.alamat2_harta}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#fff",
                  fontSize: "0.875rem",
                  "&:hover fieldset": { borderColor: "#2196f3" },
                  "&.Mui-focused fieldset": { borderColor: "#2196f3" },
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="📮 Poskod"
              name="poskod_harta"
              value={formState.poskod_harta}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#fff",
                  fontSize: "0.875rem",
                  "&:hover fieldset": { borderColor: "#2196f3" },
                  "&.Mui-focused fieldset": { borderColor: "#2196f3" },
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="🏙️ Bandar Harta"
              name="bandar_harta"
              value={formState.bandar_harta}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#fff",
                  fontSize: "0.875rem",
                  "&:hover fieldset": { borderColor: "#2196f3" },
                  "&.Mui-focused fieldset": { borderColor: "#2196f3" },
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="🗺️ Negeri Harta"
              name="negeri_harta"
              value={formState.negeri_harta}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#fff",
                  fontSize: "0.875rem",
                  "&:hover fieldset": { borderColor: "#2196f3" },
                  "&.Mui-focused fieldset": { borderColor: "#2196f3" },
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>
        </Grid>

        {/* Separator Line */}
        <Divider sx={{ my: 3, borderColor: "#ff9800" }} />

        {/* Maklumat Kewangan & Pentadbiran */}
        <Grid container spacing={2}>
          {/* Financial and administrative info */}
          <Grid item xs={12} md={6}>
            <TextField
              label="💵 Kadar Sewa (RM)"
              name="kadar_sewa"
              value={formState.kadar_sewa}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              type="number"
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#fff",
                  fontSize: "0.875rem",
                  "&:hover fieldset": { borderColor: "#ff9800" },
                  "&.Mui-focused fieldset": { borderColor: "#ff9800" },
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl
              fullWidth
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#ffffffff",
                  fontSize: "0.875rem",
                  p: 1.2,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.875rem",
                },
              }}
            >
              <InputLabel>📂 Kategori Kutipan</InputLabel>
              <Select
                label="📂 Kategori Kutipan"
                name="kategori_kutipan"
                value={formState.kategori_kutipan}
                onChange={handleChange}
                fullWidth
              >
                <MenuItem value="">
                  <em>Pilih Kategori</em>
                </MenuItem>
                {loadingCategories ? (
                  <MenuItem value="" disabled>
                    Loading...
                  </MenuItem>
                ) : (
                  categories.map((c) => (
                    <MenuItem key={`${c.id}-${c.kod_kategori}`} value={String(c.kategori_kutipan)}>
                      {c.kategori_kutipan}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="🏢 Bahagian Pemantau"
              name="bahagian_pemantau"
              value={formState.bahagian_pemantau}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
              disabled
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#f5f5f5",
                  fontSize: "0.875rem",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ ...dialogContentStyles.light, p: 2 }}>
        <Button
          variant="contained"
          onClick={handleSave}
          size="small"
          sx={{
            ...buttonStyles.save,
            m: 0,
            px: 2,
            minWidth: 0
          }}
        >
          SIMPAN
        </Button>
        <Button
          variant="contained"
          onClick={onClose}
          size="small"
          sx={{
            ...buttonStyles.cancel,
            m: 0,
            px: 2,
            minWidth: 0
          }}
        >
          BATAL
        </Button>
      </DialogActions>
    </Dialog>
  );
}

EditHarta.propTypes = {
  open: PropTypes.bool.isRequired,
  data: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default EditHarta;
